import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaCalendarDays,
  FaNoteSticky,
  FaArrowDownWideShort,
  FaArrowUpShortWide,
  FaClipboardQuestion,
  FaCircleCheck,
  FaXmark,
} from "react-icons/fa6";
import { useTranslation } from "react-i18next";

// --- Компонент однієї нотатки ---
const NoteItem = ({ item }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollHeight > 72);
    }
  }, [item.note]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString("uk-UA", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  };

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm overflow-hidden transition-all hover:border-slate-600">
      {/* Тонка акцентна смужка зверху */}
      <div className="h-[3px] w-full bg-gradient-to-r from-emerald-500/60 to-cyan-500/60" />

      <div className="p-4">
        {/* Шапка */}
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center flex-shrink-0">
              <FaNoteSticky className="text-emerald-400 w-3.5 h-3.5" />
            </div>
            <span className="text-[11px] font-semibold text-slate-500 tracking-wide">
              {formatDate(item.date)}
            </span>
          </div>

          {item.mood && (
            <div className="bg-slate-900/50 p-1 rounded-lg border border-slate-700/50">
              <img
                src={`${item.mood}.webp`}
                className="w-6 h-6 object-contain"
                alt={item.mood}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          )}
        </div>

        {/* Текст нотатки */}
        <div className="relative text-left">
          <p
            ref={textRef}
            className={`text-slate-200 text-sm leading-relaxed whitespace-pre-wrap ${
              !expanded ? "line-clamp-3 max-h-[4.5rem] overflow-hidden" : ""
            }`}
          >
            {item.note}
          </p>
          {isOverflowing && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 text-xs font-bold flex items-center gap-1 text-emerald-400 hover:text-emerald-300 transition-colors focus:outline-none uppercase tracking-wide"
            >
              {expanded ? (
                <>{t("hide", "Згорнути")} <FaChevronUp className="w-2.5 h-2.5" /></>
              ) : (
                <>{t("readMore", "Читати далі")} <FaChevronDown className="w-2.5 h-2.5" /></>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Компонент опитування ---
const SurveyItem = ({ item }) => {
  const { question, option } = item.follow_up;

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString("uk-UA", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm overflow-hidden transition-all hover:border-slate-600">
      {/* Акцентна смужка */}
      <div className="h-[3px] w-full bg-gradient-to-r from-purple-500/60 via-cyan-500/60 to-emerald-500/60" />

      <div className="p-4">
        {/* Шапка: іконка + дата */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-purple-500/15 border border-purple-500/25 flex items-center justify-center flex-shrink-0">
            <FaClipboardQuestion className="text-purple-400 w-3.5 h-3.5" />
          </div>
          <span className="text-[11px] font-semibold text-slate-500 tracking-wide">
            {formatDate(item.date)}
          </span>
        </div>

        {/* Питання */}
        <p className="text-slate-100 text-sm font-semibold leading-snug mb-3">
          {question}
        </p>

        {/* Відповідь */}
        <div className="flex items-center gap-2 bg-slate-900/60 rounded-lg px-3 py-2 border border-slate-700/60">
          <FaCircleCheck className="text-emerald-400 w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-sm font-bold text-emerald-300">{option}</span>
        </div>
      </div>
    </div>
  );
};

// --- Головний компонент ---
const Notes = ({ allMoods }) => {
  const { t } = useTranslation();

  const [viewType, setViewType] = useState("note");
  const [sortOrder, setSortOrder] = useState("newest");
  const [moodFilter, setMoodFilter] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [targetDate, setTargetDate] = useState(new Date());
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (isDatePickerOpen) setPickerYear(targetDate.getFullYear());
  }, [isDatePickerOpen, targetDate]);

  const monthNames = useMemo(() => Array.from({ length: 12 }, (_, i) =>
    new Date(2000, i, 1).toLocaleString("default", { month: "long" })
  ), []);

  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    let startYear = currentYear;
    if (allMoods && allMoods.length > 0) {
      startYear = new Date(allMoods[0].date * 1000).getFullYear();
    }
    return Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i);
  };

  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(targetDate);
    newDate.setFullYear(pickerYear);
    newDate.setMonth(monthIndex);
    setTargetDate(newDate);
    setSelectedPeriod("month");
    setIsDatePickerOpen(false);
  };

  const processedData = useMemo(() => {
    if (!allMoods) return [];
    let data = [...allMoods];

    if (selectedPeriod === "month") {
      data = data.filter((item) => {
        const d = new Date(item.date * 1000);
        return d.getMonth() === targetDate.getMonth() && d.getFullYear() === targetDate.getFullYear();
      });
    }

    data = data.filter((item) =>
      viewType === "note"
        ? item.note?.trim()
        : item.follow_up?.question && item.follow_up?.option
    );

    if (moodFilter) {
      data = data.filter((item) => Number(item.mood) === moodFilter);
    }

    data.sort((a, b) => sortOrder === "newest" ? b.date - a.date : a.date - b.date);
    return data;
  }, [allMoods, selectedPeriod, targetDate, viewType, sortOrder, moodFilter]);

  if (!allMoods) return <div className="text-center py-10 text-slate-500">{t("loading", "...")}</div>;

  return (
    <div className="max-w-2xl mx-auto w-full relative">

      {/* ПАНЕЛЬ КЕРУВАННЯ */}
      <div className="flex flex-col gap-2 mb-5 bg-slate-900/50 p-2 rounded-xl border border-slate-800 relative z-20">

        {/* Рядок 1: Перемикач тип */}
        <div className="flex bg-slate-800 p-1 rounded-lg w-full h-9">
          <button
            onClick={() => setViewType("note")}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-bold rounded transition-all ${
              viewType === "note" ? "bg-slate-700 text-white shadow" : "text-slate-500 hover:text-slate-300"
            }`}
          >
            <FaNoteSticky className="w-3 h-3 flex-shrink-0" />
            <span>{t("notes", "Нотатки")}</span>
          </button>
          <button
            onClick={() => setViewType("survey")}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-bold rounded transition-all ${
              viewType === "survey" ? "bg-slate-700 text-white shadow" : "text-slate-500 hover:text-slate-300"
            }`}
          >
            <FaClipboardQuestion className="w-3 h-3 flex-shrink-0" />
            <span>{t("surveyResults", "Опитування")}</span>
          </button>
        </div>

        {/* Рядок 2: Дата + скид + сортування — всі в одному рядку, нічого не вилізе */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (selectedPeriod === "month") setIsDatePickerOpen(!isDatePickerOpen);
              else { setSelectedPeriod("month"); setIsDatePickerOpen(true); }
            }}
            className={`flex-1 min-w-0 h-9 px-3 text-xs font-bold uppercase tracking-wider rounded-lg border transition-all flex items-center gap-2 ${
              selectedPeriod === "month"
                ? "bg-slate-700 text-emerald-400 border-emerald-500/50"
                : "bg-slate-800 text-slate-400 border-transparent hover:bg-slate-700"
            }`}
          >
            <FaCalendarDays className="w-3 h-3 flex-shrink-0" />
            <span className="truncate flex-1 text-left">
              {selectedPeriod === "month"
                ? `${targetDate.toLocaleString("default", { month: "short" })} ${targetDate.getFullYear()}`
                : t("periods.all", "Весь час")}
            </span>
            <FaChevronDown className={`w-2.5 h-2.5 flex-shrink-0 transition-transform ${isDatePickerOpen ? "rotate-180" : ""}`} />
          </button>

          {selectedPeriod === "month" && (
            <button
              onClick={() => { setSelectedPeriod("all"); setIsDatePickerOpen(false); }}
              className="h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
            >
              <FaXmark className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            onClick={() => setSortOrder((prev) => prev === "newest" ? "oldest" : "newest")}
            title={t("sort", "Сортування")}
            className="h-9 w-9 flex-shrink-0 flex items-center justify-center bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700 hover:text-white border border-transparent transition-all"
          >
            {sortOrder === "newest"
              ? <FaArrowDownWideShort className="w-4 h-4" />
              : <FaArrowUpShortWide className="w-4 h-4" />
            }
          </button>
        </div>

        {/* Рядок 3: Фільтр настрою */}
        {viewType === "note" && (
          <div className="flex items-center gap-2 px-1 py-0.5">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex-shrink-0">
              {t("mood", "Настрій")}
            </span>

            <div className="w-px h-4 bg-slate-700 flex-shrink-0" />

            <div className="flex items-center gap-2 flex-1 justify-between">
              {[1, 2, 3, 4, 5].map((mood) => (
                <button
                  key={mood}
                  onClick={() => setMoodFilter((prev) => prev === mood ? null : mood)}
                  className={`rounded-full transition-all duration-200 flex-shrink-0 ${
                    moodFilter === mood
                      ? "ring-2 ring-emerald-400 ring-offset-1 ring-offset-slate-900 scale-110"
                      : moodFilter !== null
                        ? "opacity-30 grayscale hover:opacity-80 hover:grayscale-0"
                        : "hover:scale-110"
                  }`}
                >
                  <img src={`${mood}.webp`} className="w-8 h-8 object-contain" alt={`Mood ${mood}`} />
                </button>
              ))}
            </div>

            {moodFilter !== null && (
              <button
                onClick={() => setMoodFilter(null)}
                className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-white transition-all flex items-center justify-center"
              >
                <FaXmark className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* OVERLAY DATE PICKER */}
      <div
        className={`absolute inset-x-0 top-[120px] bg-slate-900/95 backdrop-blur-md z-30 rounded-2xl border border-slate-700 transition-all duration-300 ease-in-out flex flex-col overflow-hidden shadow-2xl h-[280px] ${
          isDatePickerOpen
            ? "opacity-100 visible translate-y-0"
            : "opacity-0 invisible -translate-y-4 pointer-events-none"
        }`}
      >
        <div className="flex flex-1 h-full">
          <div className="w-1/3 border-r border-slate-700 overflow-y-auto hidden-scrollbar py-2 bg-slate-800/50">
            <div className="flex flex-col gap-1 px-2">
              {getAvailableYears().map((year) => (
                <button
                  key={year}
                  onClick={() => setPickerYear(year)}
                  className={`py-2 text-sm font-bold rounded-lg transition-colors ${
                    pickerYear === year ? "bg-emerald-600 text-white" : "text-slate-400 hover:bg-slate-700"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
          <div className="w-2/3 overflow-y-auto hidden-scrollbar py-2">
            <div className="grid grid-cols-2 gap-2 px-2">
              {monthNames.map((mName, index) => {
                const disabled =
                  pickerYear > new Date().getFullYear() ||
                  (pickerYear === new Date().getFullYear() && index > new Date().getMonth());
                return (
                  <button
                    key={index}
                    disabled={disabled}
                    onClick={() => handleMonthSelect(index)}
                    className={`py-2 text-xs font-bold rounded-lg capitalize transition-all ${
                      pickerYear === targetDate.getFullYear() && index === targetDate.getMonth()
                        ? "bg-slate-700 text-emerald-400 border border-emerald-500/50"
                        : disabled
                        ? "text-slate-700 cursor-not-allowed opacity-30"
                        : "text-slate-300 hover:bg-slate-800 bg-slate-800/30"
                    }`}
                  >
                    {mName}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* СПИСОК */}
      <div className={`space-y-3 transition-opacity duration-300 ${isDatePickerOpen ? "opacity-20 blur-sm pointer-events-none" : "opacity-100"}`}>
        {processedData.length === 0 ? (
          <div className="text-center py-16 bg-slate-800/30 rounded-2xl border border-slate-800 border-dashed">
            <FaCalendarDays className="mx-auto text-3xl text-slate-600 mb-3" />
            <p className="text-slate-500 text-sm font-medium">
              {t("noData", "Записів не знайдено")}
            </p>
            {(selectedPeriod === "month" || moodFilter) && (
              <button
                onClick={() => { setSelectedPeriod("all"); setMoodFilter(null); }}
                className="mt-2 text-emerald-500 text-xs font-bold hover:underline"
              >
                {t("clearFilters", "Очистити фільтри")}
              </button>
            )}
          </div>
        ) : (
          processedData.map((item, index) =>
            viewType === "note" ? (
              <NoteItem key={`${item.date}-${index}`} item={item} />
            ) : (
              <SurveyItem key={`${item.date}-${index}`} item={item} />
            )
          )
        )}
      </div>
    </div>
  );
};

export default Notes;